import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as SubmitIcon,
  Undo as UnapproveIcon,
  ThumbDown as RejectIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { AccountPicker, CostCentrePicker, DepartmentPicker, FiscalPeriodPicker, FiscalYearPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { BudgetStatusChip, budgetIdOf, fmtDate, getErrorMessage, unwrap } from './BudgetingShared';

// Budgets are per-line rows: one (GL account x fiscal year x optional
// period) envelope each - NOT a header with lines. Lifecycle (verified
// against accounting-apis BudgetController/BudgetService):
// DRAFT -> SUBMITTED -> APPROVED -> LOCKED, with unapprove (APPROVED ->
// SUBMITTED), reject (SUBMITTED -> DRAFT) and unlock (LOCKED -> APPROVED).
// Only DRAFT lines are editable/deletable; submit runs as maker, the rest
// of the workflow runs as checker (see config/ledger.php checker_suffixes).
const BudgetsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.account_id) {
      showSnackbar('GL account is required', 'error');
      return;
    }
    if (!values.fiscal_year_id) {
      showSnackbar('Fiscal year is required', 'error');
      return;
    }
    const payload = {
      account_id: Number(values.account_id),
      fiscal_year_id: Number(values.fiscal_year_id),
      ...(values.period_id ? { period_id: Number(values.period_id) } : {}),
      ...(values.budgeted_amount !== '' ? { budgeted_amount: Number(values.budgeted_amount) } : {}),
      ...(values.budgeted_debit !== '' ? { budgeted_debit: Number(values.budgeted_debit) } : {}),
      ...(values.budgeted_credit !== '' ? { budgeted_credit: Number(values.budgeted_credit) } : {}),
      ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
      ...(values.department_id ? { department_id: Number(values.department_id) } : {}),
      ...(values.cost_centre_id ? { cost_centre_id: Number(values.cost_centre_id) } : {}),
      ...(values.project_ref?.trim() ? { project_ref: values.project_ref.trim() } : {}),
    };
    try {
      setSaving(true);
      await ledgerAPI.createBudget(payload);
      showSnackbar('Budget line created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      console.error('Create budget failed:', error?.response?.data || error);
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    const payload = {
      ...(editing.budgeted_amount !== '' && editing.budgeted_amount != null ? { budgeted_amount: Number(editing.budgeted_amount) } : {}),
      ...(editing.budgeted_debit !== '' && editing.budgeted_debit != null ? { budgeted_debit: Number(editing.budgeted_debit) } : {}),
      ...(editing.budgeted_credit !== '' && editing.budgeted_credit != null ? { budgeted_credit: Number(editing.budgeted_credit) } : {}),
      ...(editing.period_id ? { period_id: Number(editing.period_id) } : {}),
      ...(editing.notes != null ? { notes: editing.notes } : {}),
    };
    try {
      setSaving(true);
      await ledgerAPI.updateBudget(budgetIdOf(editing), payload);
      showSnackbar('Budget line updated', 'success');
      setEditing(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getBudget(budgetIdOf(row));
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch budget'), 'error');
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this budget line?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Budget line ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const accountLabel = (b) =>
    b.account
      ? `${b.account.account_code} - ${b.account.account_name}`
      : look.accountLabel(b.account_id, b.account_id ? `Account ${b.account_id}` : '-');

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.BUDGETING}
        title="Budgets"
        subtitle="One envelope per account, year and optional period"
        addLabel="New Budget Line"
        idKey="budget_id"
        searchPlaceholder="Search budgets..."
        emptyMessage="No budget lines found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getBudgets(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { type: 'picker', key: 'fiscal_year_id', label: 'Fiscal year', picker: FiscalYearPicker },
          { type: 'picker', key: 'account_id', label: 'Account', picker: AccountPicker },
          {
            key: 'approved', label: 'Approval', width: 150,
            options: [{ label: 'Approved', value: 'true' }, { label: 'Not approved', value: 'false' }],
          },
        ]}
        columns={[
          { label: 'Account', render: (b) => <strong>{accountLabel(b)}</strong> },
          { label: 'Scope', render: (b) => (b.period_id ? look.periodLabel?.(b.period_id, `Period ${b.period_id}`) || `Period ${b.period_id}` : 'Full year'), hideOnMobile: true },
          { label: 'Budgeted', render: (b) => formatAccountingAmount(b.budgeted_amount), align: 'right' },
          { label: 'Status', render: (b) => <BudgetStatusChip status={b.status} /> },
        ]}
        rowActions={(b) => {
          const id = budgetIdOf(b);
          const status = String(b.status || 'DRAFT').toUpperCase();
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(b) }];
          if (status === 'DRAFT' && canUpdate) {
            actions.push({
              icon: EditIcon, title: 'Edit', color: 'primary',
              onClick: () => setEditing({
                ...b,
                budgeted_amount: b.budgeted_amount ?? '',
                budgeted_debit: b.budgeted_debit ?? '',
                budgeted_credit: b.budgeted_credit ?? '',
                period_id: b.period_id ?? '',
                notes: b.notes ?? '',
              }),
            });
          }
          if (status === 'DRAFT' && canCreate) {
            actions.push({ icon: SubmitIcon, title: 'Submit for approval', color: 'primary', onClick: () => runWorkflow('Submit budget', 'submitted', () => ledgerAPI.submitBudget(id)) });
          }
          if (['DRAFT', 'SUBMITTED'].includes(status) && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve budget', 'approved', () => ledgerAPI.approveBudget(id)) });
          }
          if (status === 'APPROVED' && canApprove) {
            actions.push({ icon: UnapproveIcon, title: 'Send back to submitted', color: 'default', onClick: () => runWorkflow('Unapprove budget', 'sent back to submitted', () => ledgerAPI.unapproveBudget(id)) });
            actions.push({ icon: LockIcon, title: 'Lock', color: 'default', onClick: () => runWorkflow('Lock budget', 'locked', () => ledgerAPI.lockBudget(id)) });
          }
          if (status === 'SUBMITTED' && canApprove) {
            actions.push({ icon: RejectIcon, title: 'Reject to draft', color: 'error', onClick: () => runWorkflow('Reject budget', 'rejected to draft', () => ledgerAPI.rejectBudget(id)) });
          }
          if (status === 'LOCKED' && canApprove) {
            actions.push({ icon: UnlockIcon, title: 'Unlock', color: 'primary', onClick: () => runWorkflow('Unlock budget', 'unlocked', () => ledgerAPI.unlockBudget(id)) });
          }
          if (['DRAFT', 'SUBMITTED'].includes(status) && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete', color: 'error',
              onClick: () => confirm({
                title: 'Delete budget line',
                message: 'Delete this unapproved budget line? This cannot be undone.',
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteBudget(id);
                    showSnackbar('Budget line deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Budget Line</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <AccountPicker value={values.account_id} onChange={(id) => setValues((p) => ({ ...p, account_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FiscalYearPicker value={values.fiscal_year_id} onChange={(id) => setValues((p) => ({ ...p, fiscal_year_id: id, period_id: '' }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FiscalPeriodPicker value={values.period_id} onChange={(id) => setValues((p) => ({ ...p, period_id: id }))} yearId={values.fiscal_year_id || undefined} label="Period (empty = full year)" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Budgeted Amount" value={values.budgeted_amount}
                onChange={(e) => setValues((p) => ({ ...p, budgeted_amount: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Debit Split" value={values.budgeted_debit}
                onChange={(e) => setValues((p) => ({ ...p, budgeted_debit: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Credit Split" value={values.budgeted_credit}
                onChange={(e) => setValues((p) => ({ ...p, budgeted_credit: e.target.value }))}
                inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DepartmentPicker value={values.department_id} onChange={(id) => setValues((p) => ({ ...p, department_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CostCentrePicker value={values.cost_centre_id} onChange={(id) => setValues((p) => ({ ...p, cost_centre_id: id }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Project Ref" value={values.project_ref}
                onChange={(e) => setValues((p) => ({ ...p, project_ref: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Notes" value={values.notes}
                onChange={(e) => setValues((p) => ({ ...p, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Budget Line (DRAFT only)</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth size="small" type="number" label="Budgeted Amount" value={editing.budgeted_amount ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, budgeted_amount: e.target.value }))}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FiscalPeriodPicker value={editing.period_id ?? ''} onChange={(id) => setEditing((p) => ({ ...p, period_id: id }))} yearId={editing.fiscal_year_id || undefined} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth size="small" type="number" label="Debit Split" value={editing.budgeted_debit ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, budgeted_debit: e.target.value }))}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth size="small" type="number" label="Credit Split" value={editing.budgeted_credit ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, budgeted_credit: e.target.value }))}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" multiline rows={2} label="Notes" value={editing.notes ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Budget Line {detail?.budget_id ? `#${detail.budget_id}` : ''}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{accountLabel(detail)}</strong>
              </Typography>
              <DataTable
                columns={['Field', 'Value']}
                data={[
                  ['Fiscal year', detail.fiscal_year_id ?? '-'],
                  ['Period', detail.period_id ?? 'Full year'],
                  ['Budgeted amount', formatAccountingAmount(detail.budgeted_amount)],
                  ['Debit split', formatAccountingAmount(detail.budgeted_debit)],
                  ['Credit split', formatAccountingAmount(detail.budgeted_credit)],
                  ['Department', detail.department_id ?? '-'],
                  ['Cost centre', detail.cost_centre_id ?? '-'],
                  ['Project ref', detail.project_ref || '-'],
                  ['Notes', detail.notes || '-'],
                ]}
                loading={false}
                emptyMessage="No detail."
                renderRow={(row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row[0]}</TableCell>
                    <TableCell align="right">{row[1]}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                <BudgetStatusChip status={detail.status} />
              </Box>
              {detail.status === 'DRAFT' && (
                <Typography variant="caption" color="text.secondary">
                  Created {fmtDate(detail.created_at)}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

const emptyForm = () => ({
  account_id: '',
  fiscal_year_id: '',
  period_id: '',
  budgeted_amount: '',
  budgeted_debit: '',
  budgeted_credit: '',
  notes: '',
  department_id: '',
  cost_centre_id: '',
  project_ref: '',
});

export default BudgetsTab;
